/**
 * Runtime application configuration
 * Loaded from /config.json at application startup
 */
export interface AppConfig {
  /** API base URL without path (e.g., https://portals-api.dev.mdvip.com) */
  MDVIP_API_BASE_URL: string;
  /** Idle timeout in milliseconds before showing the session warning dialog. Defaults to 1800000 (30 min). */
  IDLE_TIMEOUT_MS?: number;
  /** Duration in milliseconds of the countdown warning before auto-logout. Defaults to 20000 (20 sec). */
  WARNING_DURATION_MS?: number;
  /** URL scheme for deep link SAML callbacks on native mobile (e.g., 'mdvip-members'). Required on native platforms. */
  MOBILE_URL_SCHEME?: string;
  /** Fraction of token lifetime consumed before a proactive refresh is triggered on tab/app focus (0–1). Defaults to 0.75. */
  TOKEN_REFRESH_THRESHOLD?: number;
  /** Application Insights connection string. Omit to disable browser telemetry (e.g., local dev). */
  APPLICATIONINSIGHTS_CONNECTION_STRING?: string;
}

export type Environment = 'dev' | 'support' | 'prod' | 'local';

/**
 * Get the GraphQL endpoint URL
 */
export function getGraphqlUrl(config: AppConfig): string {
  return `${config.MDVIP_API_BASE_URL}/graphql`;
}

/**
 * Get the health check URL
 */
export function getHealthUrl(config: AppConfig): string {
  return `${config.MDVIP_API_BASE_URL}/health`;
}

/**
 * Derive the environment from the API base URL hostname
 * - portals-api.dev.mdvip.com -> dev
 * - api.support.mdvip.com -> support
 * - api.mdvip.com -> prod
 * - localhost -> local
 */
export function getEnvironment(config: AppConfig): Environment {
  try {
    const hostname = new URL(config.MDVIP_API_BASE_URL).hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
    if (hostname.includes('.dev.')) return 'dev';
    if (hostname.includes('.support.')) return 'support';
    if (hostname === 'api.mdvip.com') return 'prod';
    return 'dev'; // fallback
  } catch {
    return 'local';
  }
}
